import React, { useState, useEffect, useRef, useMemo } from 'react';
// import { Dialog, Transition } from '@headlessui/react';
// import { XIcon, PaperAirplaneIcon } from '@heroicons/react/outline';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPaperPlane, faEdit, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useComments, useAddComment, useEditComment, useMarkCommentsAsRead } from '../../hooks/useComments';
import { useUser } from '../../contexts/UserContext';
import { useTranslation } from 'react-i18next';

// Placeholder data for comments (replace with actual data fetching later)
const sampleComments = [
  { id: 3, author: 'Alice', text: 'Okay, will do.', created_at: '2024-01-15T10:10:00Z', is_unread: true, user_id: 1 },
  { id: 2, author: 'Bob', text: 'Agreed. Maybe add more detail?', created_at: '2024-01-15T10:05:00Z', is_unread: true, user_id: 2 },
  { id: 1, author: 'Alice', text: 'First comment! Looks good.', created_at: '2024-01-15T10:00:00Z', is_unread: false, user_id: 1 },
];

// Helper function to check if a comment can be edited (within the last hour)
const canEditComment = (createdAt) => {
  if (!createdAt) return false;
  const createdDate = new Date(createdAt);
  const now = new Date();
  // Comments can be edited within 1 hour of creation
  return (now - createdDate) < 3600000; // 3600000 ms = 1 hour
};

const CommentsModal = ({ isOpen, onClose, dayId, menuId }) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const [newCommentText, setNewCommentText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editText, setEditText] = useState('');
  const [isLoading, setIsLoading] = useState(false); // Add loading state
  const commentsStartRef = useRef(null); // Ref to scroll to top
  const viewedRef = useRef(false); // Track if we already marked comments as read
  const initialUnreadIdsRef = useRef(new Set());
  const isInitialLoadDoneRef = useRef(false); // Track if initial unread IDs have been set for this opening
  console.log(`[CommentsModal Render] isOpen=${isOpen}, dayId=${dayId}, initialUnreadIdsRef.current =`, initialUnreadIdsRef.current); // Log on every render

  // Use React Query hooks
  const {
    data: commentsData = [],
    isLoading: fetchLoading,
    error: fetchError,
    refetch
  } = useComments(dayId, {
    enabled: isOpen && !!dayId, // Only fetch when modal is open and dayId exists
  });

  // NEW useEffect to set initial unread IDs
  useEffect(() => {
    console.log(`[SetInitialUnread useEffect Check] isOpen=${isOpen}, dayId=${dayId}, commentsData length=${commentsData?.length}, isInitialLoadDoneRef=${isInitialLoadDoneRef.current}`);
    // Only run when the modal is open and data is loaded
    if (isOpen && dayId && commentsData && commentsData.length > 0) {
      // Find unread comments that aren't authored by current user
      const unreadIds = commentsData
        .filter(c => c.is_unread && !c.is_author)
        .map(c => c.id);
        
      console.log(`[SetInitialUnread useEffect] Found unread IDs (excluding own):`, unreadIds);
      
      // If we have new unread IDs that aren't in our initial set
      if (unreadIds.length > 0) {
        // Check if any of these unread IDs are not in our current set
        const newUnreadIds = unreadIds.filter(id => !initialUnreadIdsRef.current.has(id));
        
        if (newUnreadIds.length > 0) {
          console.log(`[SetInitialUnread useEffect] Found new unread IDs not yet in set:`, newUnreadIds);
          
          // Add the new IDs to our set
          newUnreadIds.forEach(id => initialUnreadIdsRef.current.add(id));
          console.log(`[SetInitialUnread useEffect] Updated initialUnreadIdsRef.current to:`, Array.from(initialUnreadIdsRef.current));
        }
      }
      
      // Mark that we've done the initial load for this opening cycle
      // but only if we haven't already
      if (!isInitialLoadDoneRef.current) {
        isInitialLoadDoneRef.current = true;
        console.log(`[SetInitialUnread useEffect] Set isInitialLoadDoneRef to true`);
      }
    }
  }, [isOpen, dayId, commentsData]); // Depend on commentsData to capture all responses

  const addCommentMutation = useAddComment();
  const editCommentMutation = useEditComment();
  const markAsReadMutation = useMarkCommentsAsRead();

  // When comments are loaded, mark them as read on the server
  useEffect(() => {
    console.log(`[MarkAsRead useEffect Check] isOpen=${isOpen}, dayId=${dayId}, commentsData length=${commentsData?.length}, viewedRef=${viewedRef.current}`);
    if (isOpen && dayId && commentsData && commentsData.length > 0 && !viewedRef.current) {
      // Mark as viewed to prevent repeated API calls
      console.log(`[MarkAsRead useEffect] Setting viewedRef to true for day ${dayId}`);
      viewedRef.current = true;
      
      // Mark comments as read on the server
      console.log(`[MarkAsRead useEffect] Calling markAsReadMutation for day ${dayId}, menu ${menuId}`);
      markAsReadMutation.mutate({ dayId, menuId });
      
      console.log(`[MarkAsRead useEffect] Marked ${commentsData.length} comments as read for day ${dayId}`);
    }
  }, [isOpen, dayId, commentsData, markAsReadMutation, menuId]);

  // Reset comment form and viewed status when modal closes
  useEffect(() => {
    if (!isOpen) {
      console.log(`[Cleanup useEffect] Modal closed. Current initialUnreadIdsRef:`, Array.from(initialUnreadIdsRef.current));
      
      // CRITICAL: We need to reset both refs immediately on close to prevent race conditions
      // First store a reference to the IDs we're storing
      const unreadIdsBeforeClose = Array.from(initialUnreadIdsRef.current);
      console.log(`[Cleanup useEffect] Saved unread IDs before clearing:`, unreadIdsBeforeClose);
      
      // Then clear and reset 
      initialUnreadIdsRef.current = new Set();
      isInitialLoadDoneRef.current = false; // Reset the initial load flag
      viewedRef.current = false; // Reset viewed status for next open
      
      console.log(`[Cleanup useEffect] IMMEDIATELY reset initialUnreadIdsRef, isInitialLoadDoneRef, viewedRef`);
      
      // Delay other state resets for animation
      setTimeout(() => {
        setNewCommentText('');
        setIsSending(false);
        setError(null);
        setEditingCommentId(null);
        setEditText('');
        setIsLoading(false);
        
        // DOUBLE CHECK that our refs are still cleared after the timeout
        if (initialUnreadIdsRef.current.size > 0) {
          console.error('[Cleanup useEffect] ALERT: initialUnreadIdsRef was not empty in timeout!');
          initialUnreadIdsRef.current = new Set(); // Reset again just to be sure
        }
        
        console.log(`[Cleanup useEffect] Timeout completed, all states and refs reset.`);
      }, 300); // Delay allows closing animation
    }
  }, [isOpen]);

  // Scroll to top when comments update
  useEffect(() => {
    if (isOpen && commentsStartRef.current) {
      commentsStartRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [commentsData, isOpen]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.removeEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);
  
  // Prevent background scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newCommentText.trim()) return;
    
    addCommentMutation.mutate(
      { dayId, text: newCommentText.trim() },
      {
        onSuccess: () => {
          setNewCommentText('');
          // Scroll to top to see the new comment
          if (commentsStartRef.current) {
            commentsStartRef.current.scrollIntoView({ behavior: "smooth" });
          }
        }
      }
    );
  };
  
  const handleEditComment = (comment) => {
    setEditingCommentId(comment.id);
    setEditText(comment.text);
  };
   
  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditText('');
  };
   
  const handleSaveEdit = async (commentId) => {
    if (!editText.trim()) return;
    
    editCommentMutation.mutate(
      { dayId, commentId, text: editText.trim() },
      {
        onSuccess: () => {
          setEditingCommentId(null);
          setEditText('');
        }
      }
    );
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleString(undefined, {
        dateStyle: 'short', 
        timeStyle: 'short'
      });
    } catch (e) {
      return dateString; // Fallback
    }
  }

  // Determine if a comment is an optimistic entry
  const isOptimisticComment = (comment) => {
    return comment._isOptimistic === true || (comment.id && typeof comment.id === 'string' && comment.id.startsWith('temp-'));
  };

  // Memoize the sorted comments array to prevent re-sorting on every render
  const sortedComments = useMemo(() => {
    if (!commentsData) return [];
    // Create a copy before sorting to avoid mutating the original cache
    return [...commentsData].sort((a, b) => {
      // Handle potential null/undefined dates or optimistic comments without dates
      const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
      const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
      return dateB - dateA; // Sort descending (newest first)
    });
  }, [commentsData]);

  if (!isOpen) return null;

  console.log(`[CommentsModal Pre-Render] dayId=${dayId}, initialUnreadIdsRef.current =`, Array.from(initialUnreadIdsRef.current));
  console.log(`[CommentsModal Pre-Render] All commentsData:`, JSON.stringify(commentsData, null, 2));
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 transition-opacity duration-300 ease-out backdrop-blur-sm" 
      aria-labelledby="comments-modal-title"
      role="dialog"
      aria-modal="true"
      onClick={onClose} // Close if clicking the backdrop
    >
      <div 
        className="relative w-full max-w-lg transform overflow-hidden rounded-xl bg-white p-6 text-left align-middle shadow-xl transition-all duration-300 ease-out scale-100 flex flex-col"
        style={{ maxHeight: '80vh' }}
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the modal
      >
        {/* Header with sticky positioning */}
        <div className="sticky top-0 z-10 bg-white pb-4 border-b border-gray-200">
          <h3 
            id="comments-modal-title"
            className="text-lg font-medium leading-6 text-gray-900 flex justify-between items-center"
          >
            <span className="font-semibold">{t('menu.comments', 'Comments')}</span>
            <button
              type="button"
              className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              onClick={onClose}
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faTimes} className="h-5 w-5" aria-hidden="true" />
            </button>
          </h3>
        </div>

        {/* New Comment Form - positioned at the top */}
        <form onSubmit={handleAddComment} className="flex items-start space-x-2 my-4">
          <textarea
            rows="2"
            className="flex-grow rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100 resize-none transition-colors p-3"
            placeholder={t('menu.addComment', 'Add a comment...')}
            value={newCommentText}
            onChange={(e) => setNewCommentText(e.target.value)}
            disabled={addCommentMutation.isPending}
          />
          <button
            type="submit"
            disabled={addCommentMutation.isPending || !newCommentText.trim()}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:bg-indigo-300 disabled:cursor-not-allowed h-10 mt-px transition-colors" 
            aria-label="Send comment"
          >
            {addCommentMutation.isPending ? (
                <FontAwesomeIcon icon={faSpinner} className="h-5 w-5 animate-spin" aria-hidden="true" />
            ) : (
                <FontAwesomeIcon icon={faPaperPlane} className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        </form>
        
        {/* Error messages */}
        {addCommentMutation.error && (
          <div className="text-red-500 text-sm mb-4 p-2 bg-red-50 rounded-md border border-red-200">
            {addCommentMutation.error.message || t('errors.failedToAddComment', 'Failed to add comment')}
          </div>
        )}
        {fetchError && (
          <div className="text-red-500 text-sm mb-4 p-2 bg-red-50 rounded-md border border-red-200">
            {fetchError.message || t('errors.failedToLoadComments', 'Failed to load comments')}
          </div>
        )}

        {/* Comments List with scrolling container */}
        <div className="overflow-auto flex-grow mt-2 pr-1">
          {fetchLoading ? (
              <div className="flex items-center justify-center text-gray-500 py-8">
                <FontAwesomeIcon icon={faSpinner} className="h-5 w-5 animate-spin mr-2" />
                <span>{t('common.loading', 'Loading comments...')}</span>
              </div>
          ) : commentsData.length === 0 && !fetchError ? (
              <div className="text-center text-gray-500 py-8">
                <p className="text-lg">{t('menu.noComments', 'No comments yet.')}</p>
                <p className="text-sm mt-2">{t('menu.beFirstToComment', 'Be the first to add a comment!')}</p>
              </div>
          ) : (
            <ul className="space-y-4">
              {/* Element to scroll to - positioned at the top */}
              <div ref={commentsStartRef} />
              {sortedComments.map((comment) => {
                const isInitiallyUnread = initialUnreadIdsRef.current.has(comment.id);
                const isOptimistic = isOptimisticComment(comment);
                
                // Determine className
                let liClassName = 'p-4 rounded-lg border transition-colors';
                if (isOptimistic) {
                  liClassName += ' bg-blue-50 border-blue-200 shadow-sm';
                } else if (isInitiallyUnread) {
                  liClassName += ' bg-yellow-50 border-yellow-200 shadow-sm';
                } else {
                  liClassName += ' bg-gray-50 border-gray-200';
                }

                console.log(`[Comment ID=${comment.id}] Detail: unread=${comment.is_unread}, author=${comment.is_author}, in initialUnreadIds=${isInitiallyUnread}, isOptimistic=${isOptimistic}, className="${liClassName}"`);

                return (
                <li 
                  key={comment.id} 
                  className={liClassName}
                  data-comment-id={comment.id}
                  data-is-initially-unread={isInitiallyUnread.toString()}
                >
                  {editingCommentId === comment.id ? (
                    // Edit mode
                    <div className="space-y-3">
                      <textarea
                        rows="3"
                        className="w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm transition-colors p-3"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        disabled={editCommentMutation.isPending}
                      />
                      <div className="flex justify-end gap-2">
                        <button 
                          onClick={handleCancelEdit}
                          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
                          disabled={editCommentMutation.isPending}
                        >
                          {t('common.cancel', 'Cancel')}
                        </button>
                        <button 
                          onClick={() => handleSaveEdit(comment.id)}
                          className="px-3 py-1.5 text-sm rounded-md border border-transparent bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-indigo-300 transition-colors"
                          disabled={editCommentMutation.isPending || !editText.trim()}
                        >
                          {editCommentMutation.isPending ? (
                            <span className="flex items-center">
                              <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin mr-1" />
                              {t('common.saving', 'Saving...')}
                            </span>
                          ) : (
                            t('common.save', 'Save')
                          )}
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Display mode
                    <div className="flex items-start space-x-3">
                      {/* Avatar */}
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-sm font-medium text-white shadow-sm">
                        {comment.author_name?.charAt(0).toUpperCase() || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-center flex-wrap gap-1">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {comment.author_name || t('menu.anonymousUser', 'Anonymous')}
                            {isOptimistic && (
                              <span className="ml-2 text-xs text-blue-500 italic">
                                ({t('common.sending', 'sending')})
                              </span>
                            )}
                          </p>
                          <div className="flex items-center">
                            <time dateTime={comment.created_at} className="text-xs text-gray-500">
                              {formatDate(comment.created_at)}
                              {comment.updated_at && comment.created_at !== comment.updated_at && 
                                <span className="italic ml-1">({t('common.edited', 'edited')})</span>
                              }
                            </time>
                            {/* Only show edit button if the user is the author and the comment is editable */}
                            {user && comment.user_id === user.id && canEditComment(comment.created_at) && !isOptimistic && (
                              <button 
                                onClick={() => handleEditComment(comment)} 
                                className="ml-2 text-xs text-indigo-600 hover:text-indigo-800 p-1 hover:bg-indigo-50 rounded transition-colors"
                                aria-label={t('menu.editCommentBy', 'Edit comment by {{author}}', { author: comment.author_name })}
                              >
                                <FontAwesomeIcon icon={faEdit} className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="mt-1 text-sm text-gray-700 whitespace-pre-wrap break-words">
                          {comment.text}
                        </p>
                      </div>
                    </div>
                  )}
                </li>
              )})}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommentsModal; 